/** useQueues: the open count of every queue, polled. One hook for the sidebar, Today's "Needs you" card and the top bar. */
import { useLocation } from "react-router-dom";
import { getHeldForReview } from "../../api/audienceReview";
import { getDeskPipeline } from "../../api/desk";
import { getStateLicenseSummary } from "../../api/stateLicense";
import { DEMO } from "../app/demo";
import { queueList } from "./queues";
import { useQuery } from "./useQuery";

const POLL = { intervalMs: 60_000 };

/** The held list, which gives the held count here and Today's held rows: one request for both. */
export const HELD_KEY = "v2:today:held";
export const getHeld = () => getHeldForReview();

/**
 * `enabled` false (nobody signed in) asks nothing. Demo only: `?clear=1` empties every queue, so the
 * all-clear moment reads the same in the sidebar, the bell and Today.
 */
export function useQueues(enabled = true) {
  const pipeline = useQuery(enabled ? "v2:today:pipeline" : null, getDeskPipeline, POLL);
  const licenceChanges = useQuery(enabled ? "v2:today:licence-changes" : null, getStateLicenseSummary, POLL);
  const held = useQuery(enabled ? HELD_KEY : null, getHeld, POLL);
  const { search } = useLocation();
  const cleared = DEMO && new URLSearchParams(search).get("clear") === "1";

  const loaded = queueList({
    pipeline: pipeline.data,
    licenceChanges: licenceChanges.data && { toDo: licenceChanges.data.changes, done: licenceChanges.data.done },
    held: held.data?.summary.total,
  });
  const list = cleared ? loaded.map((row) => ({ ...row, count: 0 })) : loaded;
  const failed = !pipeline.data && !!pipeline.error;
  return {
    /** Every queue that has loaded, empty ones included. */
    list,
    /** Only the queues with someone in them. */
    rows: list.filter((row) => row.count > 0),
    /** People waiting across all queues. A person in two queues counts twice: it is a count of cases. */
    open: list.reduce((sum, row) => sum + row.count, 0),
    loading: !failed && (pipeline.loading || licenceChanges.loading || held.loading),
    /** The desk queues did not load: there is nothing to list. */
    failed,
    /** The desk queues loaded but licence changes or held did not. */
    partial: !failed && (!!licenceChanges.error || !!held.error),
    refetch: () => {
      void pipeline.refetch();
      void licenceChanges.refetch();
      void held.refetch();
    },
  };
}

export type Queues = ReturnType<typeof useQueues>;
